import { ReactElement, useContext } from "react";
import { Button } from "@justworkshr/milo-core";

import { Factor } from "types/factor/factor";

import { MFA_FACTOR_COPY } from "../../constants/factorCopy";
import { addFactorMessage } from "../../constants/pageAlertsCopy";

import PageAlertContext from "contexts/PageAlertContext";
import AuthTransactionContext from "pages/register/contexts/AuthTransactionContext";

import useEnrollWebAuthnFactor from "hooks/useEnrollWebAuthnFactor";
import useEnrollWebAuthnFactorViaTransaction from "pages/register/hooks/useEnrollWebAuthnFactorViaTransaction";

import styles from "./web-authn-setup.module.css";

const WebAuthnSetup = ({
  factor,
  onCancel,
  onSuccess,
}: {
  factor: Factor;
  onCancel: () => void;
  onSuccess?: () => void;
}): ReactElement => {
  const { setAlert } = useContext(PageAlertContext);
  const { transaction, setTransaction } = useContext(AuthTransactionContext);
  const stateToken = transaction?.data.stateToken;

  const copy = MFA_FACTOR_COPY[factor.key];

  // Use the auth-transaction flow when inside the registration context so the
  // enrollment call goes through stateToken auth (no JWT required). Falls back
  // to the management API flow on the /mfa screen where the user is already
  // authenticated.
  const managementMutation = useEnrollWebAuthnFactor();
  const transactionMutation = useEnrollWebAuthnFactorViaTransaction(
    stateToken ?? "",
  );

  const handleRegister = () => {
    if (stateToken) {
      transactionMutation.mutate(undefined, {
        onSuccess: (newTransaction) => {
          setTransaction(newTransaction);
          setAlert({
            message: addFactorMessage(factor.key),
            color: "additive",
          });
          onSuccess?.();
        },
      });
    } else {
      managementMutation.mutate(undefined, {
        onSuccess: () => {
          setAlert({
            message: addFactorMessage(factor.key),
            color: "additive",
          });
          onSuccess?.();
        },
      });
    }
  };

  const activeMutation = stateToken ? transactionMutation : managementMutation;

  return (
    <div className={styles.webAuthnSetup}>
      <p className={styles.description}>{copy.setUpDetails}</p>

      {activeMutation.isError && (
        <p className={styles.error}>
          Failed to register passkey. Please try again.
        </p>
      )}

      <div className={styles.footerButtons}>
        <Button onClick={handleRegister} loading={activeMutation.isLoading}>
          Register passkey
        </Button>
        <Button
          variant="ghost"
          onClick={onCancel}
          disabled={activeMutation.isLoading}
        >
          Cancel
        </Button>
      </div>
    </div>
  );
};

export default WebAuthnSetup;
